import cn from 'classnames';
import { useCallback, useEffect } from 'react';

import { H2, P1, PrimaryButton, SecondaryButton, Anchor } from '@zooplus/ui-components';

import commonStyles from '../../../styles/common.module.scss';
import LoginBenefitsSection from '../login-benefits-section';

import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import useAuthUrls from '@hooks/use-auth-urls';
import { useTranslation } from '@hooks/use-translations';
import {
  TRACKING_DATA_EVENT,
  TARGET_COMPONENT_NAME,
  LOGIN_PAGE_ACTIONS,
  TARGET_ACTION_ID,
} from '@utilities/constants';
import { goToGuestPreview, goToLoginRegistration } from '@utilities/utils';

import styles from './login-selection-container.module.scss';

const LoginSelectionContainer = () => {
  const { t } = useTranslation();
  const { trackEventUsingTrackerListener } = usePageTracking();
  const { loginUrl, registrationUrl } = useAuthUrls();

  const handleButtonClick = useCallback(
    (actionType: (typeof LOGIN_PAGE_ACTIONS)[keyof typeof LOGIN_PAGE_ACTIONS]) => {
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.CLICK,
        TARGET_ACTION_ID[actionType as keyof typeof TARGET_ACTION_ID],
        TARGET_COMPONENT_NAME.LOGIN_SELECTION,
      );
      switch (actionType) {
        case LOGIN_PAGE_ACTIONS.CUSTOMER_LOGIN:
          loginUrl && goToLoginRegistration(loginUrl);
          break;
        case LOGIN_PAGE_ACTIONS.CUSTOMER_REGISTRATION:
          registrationUrl && goToLoginRegistration(registrationUrl);
          break;
        case LOGIN_PAGE_ACTIONS.GUEST_REGISTRATION:
          goToGuestPreview();
          break;
        default:
          console.error('Invalid type');
      }
    },
    [loginUrl, registrationUrl, trackEventUsingTrackerListener],
  );

  useEffect(() => {
    trackEventUsingTrackerListener(TRACKING_DATA_EVENT.PAGE_VIEW);
  }, [trackEventUsingTrackerListener]);

  return (
    <main className={styles.loginSelectionContainer}>
      <H2 className={commonStyles.loginSelectionHeader} data-testid="account-login-title">
        {t('checkout.login.faster.title')}
      </H2>
      <section>
        <article className={cn(styles.buttonWrapper, styles.commonWidthStyle)}>
          <PrimaryButton
            length="full"
            className={styles.commonButton}
            data-testid="btn-navigate-to-login"
            onClick={() => handleButtonClick(LOGIN_PAGE_ACTIONS.CUSTOMER_LOGIN)}>
            {t('checkout.login.button')}
          </PrimaryButton>
          <SecondaryButton
            length="full"
            className={cn(styles.commonButton, styles.createAccountButton)}
            data-testid="btn-navigate-to-register"
            onClick={() => handleButtonClick(LOGIN_PAGE_ACTIONS.CUSTOMER_REGISTRATION)}>
            {t('checkout.create.account.button')}
          </SecondaryButton>
        </article>
        <LoginBenefitsSection />
        <div className={cn(styles.dividerWrapper, styles.commonWidthStyle)}>
          <div className={styles.divider}></div>
          <P1 bold className={styles.dividerText} data-testid="divider-text">
            {t('checkout.separate.or')}
          </P1>
          <div className={styles.divider}></div>
        </div>
        <article className={cn(styles.buttonWrapper, styles.commonWidthStyle)}>
          <Anchor
            isBold
            data-testid="btn-navigate-to-guest"
            onClick={() => handleButtonClick(LOGIN_PAGE_ACTIONS.GUEST_REGISTRATION)}>
            {t('checkout.guest.login.button')}
          </Anchor>
        </article>
      </section>
    </main>
  );
};

export default LoginSelectionContainer;
